import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {HelpIcon} from '@/components/help-icon'
import {Hint} from '@/components/ui/hint'
import {ChevronGlyph} from '@/components/ui/icons'
import {ITEM_TONE} from '@/components/plan-mando'
import {ActionRow} from '@/components/plan-action-row'
import {AccionSugeridaList} from '@/components/accion-sugerida-list'
import {BateriaSolicitudBlock} from '@/components/bateria-solicitud-block'
import type {SgsstAction} from '@/hooks/sgsst/plan/use-actions'
import type {SgsstItem} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {actionRisk, formatCopRangeOf, scaleCop, standardRisk, ZERO_COP, type Cop, type MoneyRange} from '@/lib/money'
import {cn} from '@/lib/utils'

export type PlanGroup = {
  code: string | null
  item: SgsstItem | null
  actions: SgsstAction[]
}

type GroupRisk = {
  // `openShare` is the non-cumplida portion: it shrinks as actions close.
  perAction: Cop | null
  fullShare: Cop
  openShare: Cop
}

export function computeGroupRisk(
  group: PlanGroup,
  base: MoneyRange | null,
  shareByStandard: Map<string, number>
): GroupRisk {
  if (group.code === null || base === null) return {perAction: null, fullShare: ZERO_COP, openShare: ZERO_COP}
  const weightShare = shareByStandard.get(group.code) ?? 0
  const perAction = actionRisk(base, weightShare, group.actions.length)
  const nonCumplida = group.actions.filter((a) => a.status !== 'cumplida').length
  return {perAction, fullShare: standardRisk(base, weightShare), openShare: scaleCop(perAction, nonCumplida)}
}

type GrupoAcciones = {
  businessId: string
  assessmentHref: string
  planYear: number
  editable: boolean
  fase2: boolean
  onCreate: (item: SgsstItem | null, titulo?: string) => void
  onEdit: (action: SgsstAction, item: SgsstItem | null) => void
  onDelete: (action: SgsstAction) => void
  onOpenNotes: (action: SgsstAction) => void
}

function PlanGrupoSection({
  group,
  risk,
  visibleActions,
  resolvePending,
  onResolve,
  businessId,
  assessmentHref,
  planYear,
  editable,
  fase2,
  onCreate,
  onEdit,
  onDelete,
  onOpenNotes,
}: GrupoAcciones & {
  group: PlanGroup
  risk: GroupRisk
  visibleActions: SgsstAction[]
  resolvePending: boolean
  onResolve: (code: string) => void
}) {
  const {t} = useTranslation()
  const allDone = group.actions.every((a) => a.status === 'cumplida')
  const remaining = group.actions.filter((a) => a.status !== 'cumplida').length
  const resolvable = group.code !== null && group.item?.status === 'no_cumple'
  const reduced = group.actions.some((a) => a.status === 'cumplida')
  return (
    <section
      id={group.code ? `plan-${group.code}` : 'plan-transversal'}
      className={cn('surface overflow-hidden border-l-[3px]', allDone ? 'border-l-primary' : 'border-l-tone-amber-fg')}
      data-testid="plan-group">
      <div className="surface-divider flex flex-wrap items-center gap-2.5 border-b px-4 py-3">
        {group.code ? (
          <>
            <Link
              to={`${assessmentHref}#std-${group.code}`}
              data-testid="group-code"
              className="inline-flex items-center gap-1 font-mono text-xs font-semibold text-primary hover:underline">
              {group.code}
              <ChevronGlyph size={10} className="-rotate-90" />
            </Link>
            <span className="text-sm font-medium text-foreground">{group.item?.name}</span>
            {group.item?.status ? (
              <Tag tone={ITEM_TONE[group.item.status]}>{t(`sgsst.status.${group.item.status}`)}</Tag>
            ) : (
              <span className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
                {t('sgsst.status.pending')}
              </span>
            )}
            {/* Kairos no le quita el «Cumple» por su cuenta (nunca-asumir): lo dice y decide quien
                responde por el SG-SST. */}
            {group.item?.status === 'cumple' && group.actions.some((a) => a.eficacia === 'no_eficaz') && (
              <Tag tone="amber" data-testid="eficacia-en-duda">
                {t('plan.eficacia.enDuda')}
              </Tag>
            )}
          </>
        ) : (
          <>
            <span className="text-sm font-medium text-foreground">{t('plan.transversal')}</span>
            <HelpIcon label={t('common.help.iconLabelFor', {campo: t('plan.transversal')})}>
              {t('plan.transversalTip')}
            </HelpIcon>
          </>
        )}
        <span className="flex-1" />
        <div className="text-right" data-testid="group-risk">
          <p className="font-mono text-[9px] tracking-wider text-muted-foreground uppercase">
            {t('plan.risk.standardLabel')}
          </p>
          {group.code === null ? (
            <p className="font-mono text-[13px] text-muted-foreground">{t('plan.risk.transversalDash')}</p>
          ) : reduced ? (
            <p className="font-mono text-[13px] text-foreground">
              <s className="mr-1.5 text-muted-foreground">{formatCopRangeOf(risk.fullShare)}</s>
              {formatCopRangeOf(risk.openShare)}
            </p>
          ) : (
            <p className="font-mono text-[13px] text-foreground">{formatCopRangeOf(risk.fullShare)}</p>
          )}
        </div>
        {editable && fase2 && (
          <Button size="sm" variant="outline" data-testid="group-add-action" onClick={() => onCreate(group.item)}>
            {t('plan.addAction')}
          </Button>
        )}
      </div>
      {group.code === '4.1.2' && (
        <div className="px-4 pt-2.5">
          <BateriaSolicitudBlock
            businessId={businessId}
            standardStatus={group.item?.status ?? null}
            canEdit={editable}
          />
        </div>
      )}
      <div className="px-4">
        {visibleActions.map((action) => (
          <ActionRow
            key={action.id}
            businessId={businessId}
            action={action}
            planYear={planYear}
            editable={editable}
            risk={risk.perAction}
            onEdit={(a) => onEdit(a, group.item)}
            onDelete={onDelete}
            onOpenNotes={onOpenNotes}
          />
        ))}
      </div>
      {resolvable && editable && fase2 && group.item && (
        <div className="px-4">
          <AccionSugeridaList
            acciones={group.item.acciones_sugeridas}
            onCrear={(titulo) => onCreate(group.item, titulo)}
          />
        </div>
      )}
      {resolvable && editable && fase2 && (
        <div
          className="surface-divider mt-2 flex flex-wrap items-center gap-3 border-t bg-accent/40 px-4 py-2.5"
          data-testid="resolve-band">
          <Button
            size="sm"
            disabled={!allDone || resolvePending}
            onClick={() => onResolve(group.code!)}
            data-testid="resolve-button">
            {t('plan.resolve')}
          </Button>
          <Hint>{allDone ? t('plan.resolveHint') : t('plan.resolvePending', {count: remaining})}</Hint>
        </div>
      )}
      {!(resolvable && editable && fase2) && <div className="pb-1.5" />}
    </section>
  )
}

/** Vista por defecto; un orden explícito o un filtro de la gráfica la aplana en `PlanFlatList`. */
export function PlanListaAgrupada({
  grupos,
  hayTransversal,
  base,
  shareByStandard,
  visibleIds,
  resolvePending,
  onResolve,
  ...comunes
}: GrupoAcciones & {
  grupos: PlanGroup[]
  /** Sobre todos los grupos, no solo los que dejó la búsqueda. */
  hayTransversal: boolean
  base: MoneyRange | null
  shareByStandard: Map<string, number>
  /** La búsqueda recorta qué filas se pintan, nunca lo que se calcula del grupo (avance, dinero,
   * «Marcar como resuelto»). `null` sin búsqueda. */
  visibleIds: Set<string> | null
  resolvePending: boolean
  onResolve: (code: string) => void
}) {
  const {t} = useTranslation()
  return (
    <div className="flex flex-col gap-3.5">
      {grupos.map((group) => (
        <PlanGrupoSection
          key={group.code ?? 'transversal'}
          group={group}
          risk={computeGroupRisk(group, base, shareByStandard)}
          visibleActions={visibleIds ? group.actions.filter((a) => visibleIds.has(a.id)) : group.actions}
          resolvePending={resolvePending}
          onResolve={onResolve}
          {...comunes}
        />
      ))}
      {comunes.editable && comunes.fase2 && !hayTransversal && (
        <section className="surface flex flex-wrap items-center gap-2.5 px-4 py-3" id="plan-transversal">
          <span className="text-sm font-medium text-foreground">{t('plan.transversal')}</span>
          <HelpIcon label={t('common.help.iconLabelFor', {campo: t('plan.transversal')})}>
            {t('plan.transversalTip')}
          </HelpIcon>
          <span className="flex-1" />
          <Button
            size="sm"
            variant="outline"
            data-testid="transversal-add-action"
            onClick={() => comunes.onCreate(null)}>
            {t('plan.addAction')}
          </Button>
        </section>
      )}
    </div>
  )
}
